import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useToasts } from '../../../contexts/toasts';
import { useFullScreenLoader } from '../../../contexts/fullScreenLoader';
import { RagLoader } from '../../../models/LLM';
import { Alert, Badge, Button, Card, Col, Container, Modal, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { accessLevelOptions, formatDate } from '../../../utils';
import { ChunkMetadata, Llm } from '../../../api/Llm';
import { Flex, Pagination, Tooltip } from 'antd';
import UserItem from '../../../components/common/UserItem';
import { Link, useNavigate } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { sectionLabelsSelector } from '../../../slices/sectionsSlice';
import AppTag from '../../../components/common/AppTag';
import AppLoader from '../../../components/common/AppLoader';
import AppBrowsingToolbar, { Filters } from '../../../components/common/AppBrowsingToolbar';
import Select, { DefaultOptionType } from 'antd/es/select';
import { PagedResults } from '../../../models/generics';
import { EventEmitter } from '../../../utils/EventEmitter';

const DEFAULT_FILTERS: Filters = {
  'accessLevel': {
    placeholder: 'Select privacy level',
    select: true,
    query: 'accessLevel',
    type: 'string',
    options: accessLevelOptions
      .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true),
    customOptions: true,
    renderOption: (option: DefaultOptionType, index: number) => (
      <Select.Option key={index} value={option.value} label={option.label}>
        <div>
          <FontAwesomeIcon icon={option.icon} fixedWidth />
          <span className="ms-2">{option.label}</span>
        </div>
      </Select.Option>
    ),
  },
  'tags': {
    placeholder: 'Select  tags...',
    select: true,
    query: 'tags',
    type: 'string',
    options: [],
    selectProps: {
      mode: "tags",
    },
    col: 3,
  },
}

export default function LoadersList() {
  const navigate = useNavigate();

  const [loaders, setLoaders] = useState<RagLoader[]>();
  const [error, setError] = useState(false);
  const [totalLoaders, setTotalLoaders] = useState<number>();
  const [latestParams, setLatestParams] = useState<URLSearchParams>();
  const [showChunksModal, setShowChunksModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [chunkIndex, setChunkIndex] = useState<number>(0);
  const [totalChunks, setTotalChunks] = useState<number>();
  const [chunkMetadata, setChunkMetadata] = useState<ChunkMetadata>();
  const [chunkError, setChunkError] = useState(false);
  const [loader, setLoader] = useState<RagLoader>(new RagLoader());

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const loaderLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "loaders"));
  const ragsFilters: Filters = useMemo(() => {
    return {
      ...DEFAULT_FILTERS,
      'search': {
        placeholder: `Search ${loaderLabels.singular}...`,
        query: 'freeText',
      },
    }
  }, [loaderLabels]);

  const getLoaders = useCallback((queryParams?: Record<string, any>) => {
    const params = new URLSearchParams(queryParams || latestParams);
    if (queryParams) setLatestParams(params);

    Llm.getLoaders(params)
      .then(
        ({ data }) => {
          setLoaders((data as PagedResults<RagLoader>).content);
          setTotalLoaders((data as PagedResults<RagLoader>).pageMetadata.totalElements);
        },
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loaders-error', message, `${loaderLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        });
  }, [showToast, loaderLabels, latestParams]);

  const handleChunkChange = useCallback((page: number, size: number) => setChunkIndex(page), []);
  const navigateToEdit = useCallback((loader?: RagLoader) => () => navigate(`/llm/loaders/${loader ? `${loader.id}/edit` : 'create'}`), [navigate]);

  const reset = useCallback((params?: Record<string, any>) => {
    setError(false);
    setLoaders(undefined);
    getLoaders(params);
  }, [getLoaders]);

  useEffect(() => {
    EventEmitter.subscribe('browsing:paramsChanged', reset);
    return () => EventEmitter.unsubscribe('browsing:paramsChanged');
  }, [reset]);

  const handleClose = useCallback(() => {
    setShowChunksModal(false);
    setShowDeleteModal(false);
    setTotalChunks(undefined);
    setChunkError(false);
    setChunkIndex(0);
    setLoader(new RagLoader());
  }, []);

  const deleteLoader = useCallback(() => {
    showLoader();

    Llm.deleteLoader(loader!.id as number)
      .then(
        ({ data }) => {
          hideLoader();
          handleClose();
          showToast('loaders-success', `${loaderLabels.singular} deleted successfully`, `${loaderLabels.singular} Success`, 'success');
          reset();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loaders-error', message, `${loaderLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        });
  }, [showLoader, hideLoader, handleClose, loader, reset, showToast, loaderLabels]);

  const testLoader = useCallback((id: number) => () => {
    showLoader();

    Llm.testLoader(id)
      .then(
        ({ data }) => {
          hideLoader();
          data.is_working
            ? showToast('loader-test-success', `${loaderLabels.singular} is working correctly`, `${loaderLabels.singular} Tested!`, 'success')
            : showToast('loader-test-error', data.error, `${loaderLabels.singular} Test Error`, 'danger');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loader-test-error', message, `${loaderLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showLoader, hideLoader, showToast, loaderLabels]);

  const handleLoaderDelete = useCallback((loader: RagLoader) => () => {
    setLoader(loader);
    setShowDeleteModal(true);
  }, []);

  const getChunks = useCallback(() => {
    setChunkMetadata(undefined);

    const body = {
      piece_index: chunkIndex,
      splittingConf: { "chunk_size": 1000, "chunk_overlap": 50 },
    };

    Llm.getLoaderChunks(loader.id!, body)
      .then(
        ({ data }) => {
          setChunkMetadata(data)
          setTotalChunks(data.total_chunks);
        },
        (err) => {
          setChunkError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loader-chunks-error', message, `${loaderLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast, loader, chunkIndex, loaderLabels]);

  const openChunksModal = useCallback((loader: RagLoader) => () => {
    setLoader(loader);
    setShowChunksModal(true);
  }, []);

  useEffect(() => {
    if (loader && showChunksModal) getChunks();
  }, [showChunksModal, chunkIndex, loader, getChunks]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    );

    if (!loaders) return <AppLoader className="py-5 my-5" justify="center" size={100} />;

    return loaders.length === 0
      ? (
        <Alert className="py-2" variant="info">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No {loaderLabels.plural} found.</span>
        </Alert>
      )
      : (
        <Row>
          {loaders?.map((loader, index) => (
            <Col key={loader.id} md={3} className="px-2 mb-3">
              <Card className="shadow-sm h-100">
                <div className="d-flex justify-content-between p-1">
                  <Badge className="shadow w-auto">{loader.accessLevel}</Badge>
                  <Badge bg="none" className="bg-pink shadow w-auto">{loader.type}</Badge>
                </div>
                <Card.Body className="py-2">
                  <Card.Title className="mb-1">
                    <Link to="#" onClick={openChunksModal(loader)}>{loader.name}</Link>
                  </Card.Title>
                  <p className="line-clamp-2 text-secondary mb-2">{loader.description}</p>

                  <Flex className="tags-wrapper mb-3">
                    {loader.tags.length === 0 && <AppTag noTags />}
                    {loader.tags.map(tag => <AppTag key={tag} tag={tag} />)}
                  </Flex>

                  <Row className="justify-content-center">
                    <Tooltip title={`Test ${loaderLabels.singular}`}>
                      <button className="circle-button dark" onClick={testLoader(loader.id!)}>
                        <FontAwesomeIcon icon={solid('cogs')} />
                      </button>
                    </Tooltip>
                    <Tooltip title={`Edit ${loaderLabels.singular}`}>
                      <button className="circle-button dark ms-2" onClick={navigateToEdit(loader)}>
                        <FontAwesomeIcon icon={solid('pen')} />
                      </button>
                    </Tooltip>
                    <Tooltip title={`Delete ${loaderLabels.singular}`}>
                      <button className="circle-button dark ms-2" onClick={handleLoaderDelete(loader)}>
                        <FontAwesomeIcon icon={solid('trash')} />
                      </button>
                    </Tooltip>
                  </Row>
                </Card.Body>
                <UserItem className="border rounded p-1 m-2" user={loader.owner!} organization={loader.organization!} />
                <small className="ms-1 font-sm text-muted">
                  <b>Created:</b>
                  <span className="ms-1">{formatDate(loader.createDateTime!)}</span>
                </small>
              </Card>
            </Col>
          ))}
        </Row>
      )
  }, [loaders, loaderLabels, openChunksModal, testLoader, navigateToEdit, handleLoaderDelete, error]);

  return (
    <Container className="mt-3">
      <AppBrowsingToolbar
        title={`${loaderLabels.plural} List`}
        actions={
          <Button size="sm" variant="success" onClick={navigateToEdit()}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="d-none d-sm-inline ms-2">Create {loaderLabels.singular}</span>
          </Button>
        }
        total={totalLoaders}
        filters={ragsFilters}
      />
      <hr />

      {renderAsyncContent()}

      <Modal show={showChunksModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>'{loader?.name}' Chunks</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {chunkError
            ? (
              <Alert variant="danger" className="py-2">
                <FontAwesomeIcon icon={solid('exclamation-triangle')} />
                <span className="ms-2">An error occurred, try again later.</span>
              </Alert>
            )
            : (
              <>
                <Flex justify="center" className="mb-2">
                  <Pagination
                    simple
                    current={chunkIndex}
                    total={totalChunks}
                    pageSize={1}
                    onChange={handleChunkChange}
                    showSizeChanger={false}
                    disabled={!chunkMetadata}
                  />
                </Flex>

                {chunkMetadata
                  ? (
                    <div data-color-mode="light">
                      <MDEditor.Markdown className="p-2 border rounded bg-transparent" source={chunkMetadata.chunk} />
                    </div>
                  )
                  : <AppLoader className="py-5" justify="center" size={100} />
                }
              </>
            )
          }
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {loaderLabels.singular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {loaderLabels.singular} <b>{loader?.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteLoader}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}